import { COMMANDS, RIBBON_TABS, ribbonGroups, runCommand } from '../commands/registry';
import { useStore, setState, openModal } from '../state/store';
import { CommandButton, commandTip } from './CommandButton';
import { openSearch } from '../app/actions';
import { Icon } from './common';
import { tip } from './Tooltip';
import type { NoteDragPayload } from './noteDrag';
import type { DurBase } from '@simplenotation/theory';
import type { RibbonTab } from '../commands/types';

export function Ribbon() {
  const tab = useStore((s) => s.ribbonTab);
  const open = useStore((s) => s.ribbonOpen);
  const voice = useStore((s) => s.ed.cursor.voice);
  const groups = ribbonGroups(tab as RibbonTab);
  return (
    <div className="ribbon" data-tour="ribbon">
      <div className="ribbon-tabs" role="tablist" aria-label="Notation categories">
        {RIBBON_TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id && open}
            className={`ribbon-tab${tab === t.id && open ? ' on' : ''}`}
            onClick={() => setState((s) => ({ ribbonTab: t.id, ribbonOpen: s.ribbonTab === t.id ? !s.ribbonOpen : true }))}
          >
            {t.label}
          </button>
        ))}
        <span className="spacer" />
        <button className="ribbon-tab" onClick={() => openModal('versions')}>
          Versions
        </button>
        <button className="ribbon-tab" onClick={() => openModal('instruments')}>
          Instruments
        </button>
        <div className="voices" role="radiogroup" aria-label="Voice">
          {[1, 2, 3, 4].map((v) => (
            <button key={v} role="radio" aria-checked={voice === v - 1} className={`voice voice-${v}${voice === v - 1 ? ' on' : ''}`} {...commandTip(`voice.${v}`, { hint: 'Notes in different voices share the staff, e.g. a melody over a bass line.' })} onClick={() => runCommand(`voice.${v}`)}>
              {v}
            </button>
          ))}
        </div>
      </div>
      {open && (
        <div className="ribbon-commands" role="toolbar" aria-label={`${RIBBON_TABS.find((t) => t.id === tab)?.label} commands`}>
          {groups.map((g) => (
            <div key={g.label || 'other'} className="ribbon-group" role="group" aria-label={g.label || undefined}>
              <div className="ribbon-group-cmds">
                {g.cmds.map((c) => (
                  <CommandButton key={c.id} id={c.id} />
                ))}
              </div>
              {g.label && (
                <div className="ribbon-group-label" aria-hidden="true">
                  {g.label}
                </div>
              )}
            </div>
          ))}
          <span className="spacer" />
          <button className="cmd ghost" aria-label="Search all commands" {...commandTip('app.search', { hint: 'Find any command by name.' })} onClick={() => openSearch()}>
            <Icon icon={{ svg: 'M10.5 17a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13zM15.5 15.5L21 21' }} size={18} />
          </button>
          <button className="cmd ghost" aria-label="Collapse ribbon" {...tip('Collapse the toolbar', { hint: 'Click a tab name to open it again.' })} onClick={() => setState({ ribbonOpen: false })}>
            ×
          </button>
        </div>
      )}
    </div>
  );
}

/** Note values and the rest can be dragged from the palette onto the score. */
function dragPayload(id: string): NoteDragPayload | undefined {
  if (id.startsWith('duration.')) return { base: Number(id.slice('duration.'.length)) as DurBase };
  if (id === 'note.rest') return { rest: true };
  return undefined;
}

export function Palette() {
  const recent = useStore((s) => s.recent);
  const groups: { title: string; ids: string[] }[] = [
    { title: 'Durations', ids: ['duration.1', 'duration.2', 'duration.4', 'duration.8', 'duration.16', 'duration.32'] },
    { title: 'Accidentals', ids: ['note.accidental.sharp', 'note.accidental.flat', 'note.accidental.natural', 'note.enharmonic'] },
    { title: 'Ties & more', ids: ['note.tieNext', 'note.tiePrev', 'note.slur', 'note.dot', 'note.rest', 'note.grace', 'note.triplet'] },
  ];
  const recentIds = recent.filter((id) => COMMANDS.some((c) => c.id === id && !c.palette)).slice(0, 6);
  return (
    <nav className="palette" aria-label="Common notations" data-tour="palette">
      <button className="palette-search" onClick={() => openSearch()} {...commandTip('app.search', { hint: 'Find any command by name.', side: 'right' })} data-tour="search">
        <Icon icon={{ svg: 'M10.5 17a6.5 6.5 0 1 1 0-13 6.5 6.5 0 0 1 0 13zM15.5 15.5L21 21' }} size={16} />
        <span>Search</span>
      </button>
      {groups.map((g) => (
        <div key={g.title} className="palette-group" role="group" aria-label={g.title}>
          {g.ids.map((id) => (
            <CommandButton key={id} id={id} size={24} drag={dragPayload(id)} tipSide="right" />
          ))}
        </div>
      ))}
      {recentIds.length > 0 && (
        <div className="palette-group recent" role="group" aria-label="Recently used">
          <div className="palette-title">Recent</div>
          {recentIds.map((id) => (
            <CommandButton key={id} id={id} size={20} tipSide="right" />
          ))}
        </div>
      )}
    </nav>
  );
}
